import React, { useState, useRef, useMemo, useEffect } from "react";
import {
  FaTimes,
  FaDownload,
  FaSpinner,
  FaUsers,
  FaSchool,
} from "react-icons/fa";
import { toast } from "react-toastify";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

const PHOTOS_PER_PAGE = 30; // Exactly 6 columns x 5 rows = 30 photos per page

export const ClassPhotoPdfModal = ({
  isOpen,
  onClose,
  schoolName = "",
  className = "",
  students = [],
  allClassFolders = null, // Object if multi-class: { "9th B": [...], "10th A": [...] }
}) => {
  const classNames = useMemo(() => {
    if (allClassFolders && typeof allClassFolders === "object") {
      return Object.keys(allClassFolders);
    }
    return [];
  }, [allClassFolders]);

  const [activeClass, setActiveClass] = useState(className || (classNames[0] || ""));
  const [downloading, setDownloading] = useState(false);
  const [loadingImages, setLoadingImages] = useState(false);
  const [conversionProgress, setConversionProgress] = useState({ done: 0, total: 0 });
  const [base64Images, setBase64Images] = useState({});
  const pdfContainerRef = useRef(null);

  // Sync activeClass when props change
  useEffect(() => {
    if (className) {
      setActiveClass(className);
    } else if (classNames.length > 0) {
      setActiveClass(classNames[0]);
    }
  }, [className, classNames]);

  // Determine active student list
  const currentStudents = useMemo(() => {
    if (allClassFolders && activeClass && allClassFolders[activeClass]) {
      return allClassFolders[activeClass];
    }
    return students || [];
  }, [allClassFolders, activeClass, students]);

  // Clean filename generator preserving Hindi/Marathi/Unicode text
  const sanitizeFilename = (name) => {
    if (!name) return "";
    return name
      .replace(/[/\\?%*:|"<>]/g, " ") // only remove illegal filesystem characters
      .replace(/\s+/g, " ")
      .trim();
  };

  // Format class header (e.g. "Class : 9th B" or "Class : इयत्ता नववी – वर्ग अ")
  const formatClassHeader = (cls) => {
    if (!cls) return "Class";
    const trimmed = cls.trim();
    if (trimmed.toLowerCase().startsWith("class")) {
      return trimmed.includes(":") ? trimmed : trimmed.replace(/class\s*/i, "Class : ");
    }
    return `Class : ${trimmed}`;
  };

  const displayClassHeader = formatClassHeader(activeClass);
  const displayClassLabel = activeClass
    ? activeClass.toLowerCase().startsWith("class")
      ? activeClass
      : `Class ${activeClass}`
    : "Class";

  const downloadFilename = `${sanitizeFilename(schoolName || "School")} - ${sanitizeFilename(displayClassLabel)}.pdf`;

  // Get real photo number formatted with blank space after 3 digits (e.g. "102 830", "123 456" - no hyphen)
  const getPhotoLabel = (st) => {
    if (!st) return "";
    let raw = "";
    if (st.photo_no) {
      raw = String(st.photo_no);
    } else if (st.photo_number) {
      raw = String(st.photo_number);
    } else if (st.student_id) {
      const parts = String(st.student_id).split("-");
      if (parts.length > 1 && parts[1]) {
        raw = parts[1];
      } else {
        raw = String(st.student_id);
      }
    } else if (st.photo) {
      const match = String(st.photo).match(/photo_[^\-_]+-([^_.]+)/);
      if (match && match[1]) {
        raw = match[1];
      }
    }

    const str = String(raw).trim();
    if (!str) return "";

    // Extract all digits (removing any existing hyphen, space, or special characters)
    const digits = str.replace(/\D/g, "");
    if (digits.length === 6) {
      return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    }
    if (digits.length > 6) {
      const last6 = digits.slice(-6);
      return `${last6.slice(0, 3)} ${last6.slice(3)}`;
    }
    // Fallback: replace any hyphen with space
    return str.replace(/-/g, " ");
  };



  // Helper to get local proxied URL when running on localhost
  const getProxiedUrl = (url) => {
    if (!url) return "";
    if (
      (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") &&
      url.startsWith("https://sramidcard.in/")
    ) {
      return url.replace("https://sramidcard.in", "");
    }
    return url;
  };

  // Convert an image URL to Base64 using fetch + blob or Image element canvas
  const fetchImageAsBase64 = async (photoUrl, downloadUrl) => {
    // Priority: downloadUrl (PHP endpoint with Access-Control-Allow-Origin: *), then proxied, then photoUrl
    const candidateUrls = [
      downloadUrl,
      getProxiedUrl(downloadUrl),
      getProxiedUrl(photoUrl),
      photoUrl,
    ].filter(Boolean);

    // Method 1: Fetch API
    for (const u of candidateUrls) {
      try {
        const res = await fetch(u, { mode: "cors" });
        if (res.ok) {
          const blob = await res.blob();
          const dataUri = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
          });
          if (dataUri && dataUri.startsWith("data:image")) {
            return dataUri;
          }
        }
      } catch (e) {
        // continue to next candidate
      }
    }

    // Method 2: HTML Image + Canvas with crossOrigin anonymous
    for (const u of candidateUrls) {
      try {
        const dataUri = await new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            try {
              const canvas = document.createElement("canvas");
              canvas.width = img.naturalWidth || img.width;
              canvas.height = img.naturalHeight || img.height;
              const ctx = canvas.getContext("2d");
              ctx.drawImage(img, 0, 0);
              resolve(canvas.toDataURL("image/jpeg", 0.95));
            } catch {
              resolve(null);
            }
          };
          img.onerror = () => resolve(null);
          img.src = u;
        });
        if (dataUri && dataUri.startsWith("data:image")) {
          return dataUri;
        }
      } catch {
        // continue
      }
    }

    // Fallback: return direct URL
    return downloadUrl || photoUrl || "";
  };

  // Pre-convert all images to Base64 when modal opens or activeClass changes
  useEffect(() => {
    if (!isOpen || currentStudents.length === 0) {
      setBase64Images({});
      return;
    }

    let isMounted = true;
    setLoadingImages(true);
    setConversionProgress({ done: 0, total: currentStudents.length });

    const convertAll = async () => {
      const map = {};
      let completedCount = 0;

      // Process in batches of 6 concurrent requests for fast, reliable loading
      const batchSize = 6;
      for (let i = 0; i < currentStudents.length; i += batchSize) {
        if (!isMounted) return;
        const chunk = currentStudents.slice(i, i + batchSize);

        await Promise.all(
          chunk.map(async (st) => {
            const studentId = st.student_id;
            const photoUrl = st.photo;
            const downloadUrl = st.download_photo;

            if (!photoUrl && !downloadUrl) return;

            const base64 = await fetchImageAsBase64(photoUrl, downloadUrl);
            if (base64 && isMounted) {
              map[studentId] = base64;
            }

            completedCount++;
            if (isMounted) {
              setConversionProgress({ done: completedCount, total: currentStudents.length });
            }
          })
        );
      }

      if (isMounted) {
        setBase64Images(map);
        setLoadingImages(false);
      }
    };

    convertAll();

    return () => {
      isMounted = false;
    };
  }, [isOpen, currentStudents, activeClass]);

  // Chunk students into pages of 30 (6 columns x 5 rows)
  const pages = useMemo(() => {
    const list = currentStudents || [];
    if (list.length === 0) return [[]];

    const result = [];
    for (let i = 0; i < list.length; i += PHOTOS_PER_PAGE) {
      result.push(list.slice(i, i + PHOTOS_PER_PAGE));
    }
    return result;
  }, [currentStudents]);

  const totalPages = pages.length;

  if (!isOpen) return null;

  // Direct High-Resolution Multi-Page PDF Download via jsPDF + html2canvas
  // Each A4 page is rendered individually to guarantee exact page count and NO blank pages
  const handleDownloadPdf = async () => {
    if (!pdfContainerRef.current) return;
    setDownloading(true);
    toast.info("Generating PDF with original photos... Please wait.");

    try {
      const container = pdfContainerRef.current;
      const pageElements = container.querySelectorAll(".a4-page-sheet");

      if (!pageElements || pageElements.length === 0) {
        throw new Error("No A4 sheets found in container");
      }

      // Ensure all images are fully loaded
      const allImgs = Array.from(container.querySelectorAll("img"));
      await Promise.all(
        allImgs.map((img) => {
          if (img.complete) return Promise.resolve();
          return new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
          });
        })
      );

      // Create jsPDF instance with exact A4 portrait dimensions
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pdfWidth = pdf.internal.pageSize.getWidth(); // 210 mm
      const pdfHeight = pdf.internal.pageSize.getHeight(); // 297 mm

      for (let i = 0; i < pageElements.length; i++) {
        const pageEl = pageElements[i];

        // Add a new page for subsequent sheets (page 0 is created by default)
        if (i > 0) {
          pdf.addPage("a4", "portrait");
        }

        // Render each page sheet with 2x scale for sharp, high-res photos with natural proportions
        const canvas = await html2canvas(pageEl, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          backgroundColor: "#ffffff",
          scrollX: 0,
          scrollY: 0,
          windowWidth: 794,
        });

        const imgData = canvas.toDataURL("image/jpeg", 0.95);
        pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);
      }

      // Download file with exact School Name and Class Name
      pdf.save(downloadFilename);
      toast.success(`PDF "${downloadFilename}" downloaded successfully!`);
    } catch (err) {
      console.error("PDF direct export error:", err);
      toast.error("Failed to generate PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="pdf-modal-overlay" onClick={onClose}>
      <div className="pdf-modal-wrapper" onClick={(e) => e.stopPropagation()}>
        {/* Top Control Bar */}
        <div className="pdf-modal-header">
          <div className="header-info">
            <div className="header-badge-school">
              <FaSchool />
              <span>{schoolName || "Bhagwan Vidyalaya Beed"}</span>
            </div>
            <div className="header-badge-class">
              <FaUsers />
              <span>{displayClassLabel}</span>
            </div>
            <span className="stats-pill">
              {currentStudents.length} Students • {totalPages} {totalPages === 1 ? "Page" : "Pages"} (30 / Page)
            </span>
            {loadingImages && (
              <span className="stats-pill loading-pill">
                <FaSpinner className="spin-icon" /> Photos: {conversionProgress.done}/{conversionProgress.total}
              </span>
            )}
          </div>

          <div className="header-actions-right">
            {/* If school has multiple classes, show switcher */}
            {classNames.length > 1 && (
              <select
                className="class-selector-dropdown"
                value={activeClass}
                onChange={(e) => setActiveClass(e.target.value)}
                title="Select Class"
              >
                {classNames.map((cls) => (
                  <option key={cls} value={cls}>
                    {cls} ({allClassFolders[cls]?.length || 0} photos)
                  </option>
                ))}
              </select>
            )}

            <button
              className="pdf-action-btn btn-download-pdf"
              onClick={handleDownloadPdf}
              disabled={downloading}
              title={`Download PDF: ${downloadFilename}`}
            >
              {downloading ? <FaSpinner className="spin-icon" /> : <FaDownload />}
              <span>{downloading ? "Preparing..." : "Download PDF"}</span>
            </button>

            <button
              className="pdf-action-btn btn-close-modal"
              onClick={onClose}
              title="Close Preview"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Modal Scrollable Body */}
        <div className="pdf-modal-body">
          <div className="a4-preview-scroll-wrapper">
            {/* Printable A4 Container */}
            <div
              className="printable-sheets-container"
              ref={pdfContainerRef}
              id="printable-sheets-container"
            >
              {pages.map((studentChunk, pageIdx) => {
                const pageNumber = pageIdx + 1;

                return (
                  <div
                    key={`page-${pageIdx}`}
                    className="a4-page-sheet"
                    id={`a4-page-${pageNumber}`}
                  >
                    {/* Header matching Demo Image:
                        Left: Navy Blue "School Name : [Name]"
                        Right: Light Blue "Class : [Class]"
                    */}
                    <div className="pdf-sheet-header">
                      <div className="header-box-school">
                        <span>School Name : {schoolName || "Bhagwan Vidyalaya Beed"}</span>
                      </div>
                      <div className="header-box-class">
                        <span>{displayClassHeader}</span>
                      </div>
                    </div>

                    {/* Photo Grid: EXACTLY 6 Columns x 5 Rows = 30 Slots */}
                    <div className="pdf-sheet-grid">
                      {studentChunk.map((st, sIdx) => {
                        const studentId = st.student_id || `ST-${sIdx + 1}`;
                        // Use base64 if available, otherwise fallback to download_photo or photo
                        const imgSrc =
                          base64Images[studentId] ||
                          st.download_photo ||
                          st.photo;

                        return (
                          <div key={studentId || sIdx} className="pdf-student-card">
                            <div className="student-photo-wrapper">
                              {imgSrc ? (
                                <img
                                  src={imgSrc}
                                  alt={`Student ${studentId}`}
                                  className="student-photo-img"
                                  loading="eager"
                                  onError={(e) => {
                                    // Fallback to alternative URL if current src fails
                                    const altSrc = st.download_photo || st.photo;
                                    if (e.target.src !== altSrc && altSrc) {
                                      e.target.src = altSrc;
                                    }
                                  }}
                                />
                              ) : (
                                <div className="empty-photo-placeholder" />
                              )}
                            </div>
                            <div className="student-photo-label" title={`Photos No : ${getPhotoLabel(st)}`}>
                              <span>Photos No : {getPhotoLabel(st)}</span>
                            </div>
                          </div>
                        );
                      })}

                      {/* Empty filler slots if last page has less than 30 photos (to maintain 6x5 layout) */}
                      {
                        studentChunk.length < PHOTOS_PER_PAGE &&
                        Array.from({ length: PHOTOS_PER_PAGE - studentChunk.length }).map(
                          (_, emptyIdx) => (
                            <div
                              key={`empty-${emptyIdx}`}
                              className="pdf-student-card empty-card"
                            >
                              <div className="empty-photo-placeholder" />
                              <div className="student-photo-label empty-label">
                                <span>-</span>
                              </div>
                            </div>
                          )
                        )
                      }
                    </div>

                    {/* Footer Banner matching Demo Image:
                        Navy Blue banner with "———— To Be Continue Next Pages : 1/3 ————"
                    */}
                    <div className="pdf-sheet-footer">
                      <div className="footer-banner-rule"></div>
                      <div className="footer-banner-content">
                        <span>
                          To Be Continue Next Pages : {pageNumber}/{totalPages}
                        </span>
                      </div>
                      <div className="footer-banner-rule"></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassPhotoPdfModal;
